import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { journalAPI } from '../services/api';
import type { JournalEntryCreate, JournalEntryUpdate, JournalEntrySource } from '../types';

interface JournalFilters {
  source?: JournalEntrySource;
  period_type?: string;
  period_id?: string;
  limit?: number;
  offset?: number;
}

export function useJournal(filters: JournalFilters = {}) {
  const queryClient = useQueryClient();

  const queryKey = ['journal', filters];

  const { data: entries = [], isLoading, error } = useQuery({
    queryKey,
    queryFn: () => journalAPI.getAll(filters),
  });

  const createMutation = useMutation({
    mutationFn: (entry: JournalEntryCreate) => journalAPI.create(entry),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journal'] });
    },
  });

  const createBulkMutation = useMutation({
    mutationFn: (entries: JournalEntryCreate[]) => journalAPI.createBulk(entries),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journal'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: JournalEntryUpdate }) =>
      journalAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journal'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => journalAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journal'] });
    },
  });

  return {
    entries,
    isLoading,
    error: error ? String(error) : null,
    createEntry: createMutation.mutateAsync,
    createBulk: createBulkMutation.mutateAsync,
    updateEntry: (id: string, data: JournalEntryUpdate) =>
      updateMutation.mutateAsync({ id, data }),
    deleteEntry: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
  };
}
